import { useMemo, useState } from "react";

import {
  DeleteDeliveryLocationBody,
  useCreateDeliveryLocation,
  useDeliveryLocations,
  useUpdateDeliveryLocation
} from "../../../../apis";
import {
  DeleteDeliveryLocationModal,
  DeliveryLocationColumn
} from "../../../../components";

import styles from "./LocationsTabContent.module.scss";

const LocationsTabContent = () => {
  const { deliveryLocations, isLoading } = useDeliveryLocations();

  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);

  const [deletingLocation, setDeletingLocation] =
    useState<DeleteDeliveryLocationBody | null>(null);

  const countries = useMemo(() => {
    return deliveryLocations?.countries.map((country) => ({
      name: country.name,
      canBeDeleted: country.cities.length === 0
    }));
  }, [deliveryLocations]);

  const cities = useMemo(() => {
    return deliveryLocations?.countries
      .find((country) => country.name === selectedCountry)
      ?.cities.map((city) => ({
        name: city.name,
        canBeDeleted: city.areas.length === 0
      }));
  }, [deliveryLocations, selectedCountry]);

  const areas = useMemo(() => {
    return deliveryLocations?.countries
      .find((country) => country.name === selectedCountry)
      ?.cities.find((city) => city.name === selectedCity)
      ?.areas.map((area) => ({
        name: area.name,
        deliveryZoneId: area.deliveryZoneId,
        canBeDeleted: !area.deliveryZoneId
      }));
  }, [deliveryLocations, selectedCountry, selectedCity]);

  const { mutate: createDeliveryLocation } = useCreateDeliveryLocation();
  const { mutate: updateDeliveryLocation } = useUpdateDeliveryLocation();

  return (
    <div className={styles.columnsContainer}>
      <DeliveryLocationColumn
        title="Countries"
        isLoading={isLoading}
        locationsList={countries}
        selectedLocation={selectedCountry}
        onLocationSelect={(location) => {
          setSelectedCountry(location);
          setSelectedCity(null);
        }}
        onLocationCreate={(location) => {
          createDeliveryLocation({ countryName: location.trim() });
        }}
        onLocationUpdate={({ existingName, newName }) => {
          updateDeliveryLocation({
            existingCountryName: existingName,
            newCountryName: newName.trim()
          });
        }}
        onLocationDelete={(location) => {
          setDeletingLocation({ countryName: location });
        }}
      />
      <DeliveryLocationColumn
        title="Cities"
        isLoading={isLoading}
        isDisabled={!selectedCountry}
        locationsList={cities}
        selectedLocation={selectedCity}
        onLocationSelect={(location) => {
          setSelectedCity(location);
        }}
        onLocationCreate={(location) => {
          if (selectedCountry) {
            createDeliveryLocation({
              countryName: selectedCountry,
              cityName: location.trim()
            });
          }
        }}
        onLocationUpdate={({ existingName, newName }) => {
          if (selectedCountry) {
            updateDeliveryLocation({
              countryName: selectedCountry,
              existingCityName: existingName,
              newCityName: newName.trim()
            });
          }
        }}
        onLocationDelete={(location) => {
          if (selectedCountry) {
            setDeletingLocation({
              countryName: selectedCountry,
              cityName: location
            });
          }
        }}
      />
      <DeliveryLocationColumn
        title="Areas"
        isLoading={isLoading}
        isDisabled={!selectedCountry || !selectedCity}
        locationsList={areas}
        onLocationCreate={(location) => {
          if (selectedCountry && selectedCity) {
            createDeliveryLocation({
              countryName: selectedCountry,
              cityName: selectedCity,
              areaName: location.trim()
            });
          }
        }}
        onLocationUpdate={({ existingName, newName }) => {
          if (selectedCountry && selectedCity) {
            updateDeliveryLocation({
              countryName: selectedCountry,
              cityName: selectedCity,
              existingAreaName: existingName,
              newAreaName: newName.trim()
            });
          }
        }}
        onLocationDelete={(location) => {
          if (selectedCountry && selectedCity) {
            setDeletingLocation({
              countryName: selectedCountry,
              cityName: selectedCity,
              areaName: location
            });
          }
        }}
      />

      {deletingLocation && (
        <DeleteDeliveryLocationModal
          isOpen={!!deletingLocation}
          location={deletingLocation}
          onClose={() => setDeletingLocation(null)}
        />
      )}
    </div>
  );
};

export default LocationsTabContent;
